<template>
  <section class="customer-detail-tabs-panel">
    <AppSectionTabs
      v-if="showSubTabs"
      v-model="currentSubTab"
      :tabs="currentSubTabs"
      :aria-label="text.detail.panel.subSectionsAriaLabel"
    />

    <component
      :is="currentTabComponent"
      :customer="customer"
      :customer-metrics="customerMetrics"
      :customer-overview="customerOverview"
      :persons="persons"
      :default-billing-data="defaultBillingData"
      @edit-section="$emit('edit-section', $event)"
      @add-contact="$emit('add-contact')"
      @edit-contact="$emit('edit-contact', $event)"
      @delete-contact="$emit('delete-contact', $event)"
      @edit-billing-data="$emit('edit-billing-data')"
      @open-billing-data-list="$emit('open-billing-data-list')"
      @create-billing-data="$emit('create-billing-data')"
      @view-sale-detail="$emit('view-sale-detail', $event)"
      @change-sale-status="$emit('change-sale-status', $event)"
      @open-history="$emit('open-history')"
      @open-permissions="$emit('open-permissions')"
      @open-merge="$emit('open-merge')"
      @archive-toggle="$emit('archive-toggle')"
      @delete-customer="$emit('delete-customer')"
      @update-count="handleBusinessCountUpdate"
    />
  </section>
</template>

<script>
import AppSectionTabs from '@/components/common/AppSectionTabs.vue';
import CustomerDetailOverviewTab from '@/components/customers/detail/tabs/CustomerDetailOverviewTab.vue';
import CustomerDetailInformationTab from '@/components/customers/detail/tabs/CustomerDetailInformationTab.vue';
import CustomerDetailSalesTab from '@/components/customers/detail/tabs/CustomerDetailSalesTab.vue';
import CustomerDetailRetainersTab from '@/components/customers/detail/tabs/CustomerDetailRetainersTab.vue';
import CustomerDetailMultiCustomerTab from '@/components/customers/detail/tabs/CustomerDetailMultiCustomerTab.vue';
import CustomerDetailInvoicesTab from '@/components/customers/detail/tabs/CustomerDetailInvoicesTab.vue';
import CustomerDetailCreditNotesTab from '@/components/customers/detail/tabs/CustomerDetailCreditNotesTab.vue';
import CustomerDetailSimpleNotesTab from '@/components/customers/detail/tabs/CustomerDetailSimpleNotesTab.vue';
import CustomerDetailPaymentsTab from '@/components/customers/detail/tabs/CustomerDetailPaymentsTab.vue';
import CustomerDetailFinanceAllTab from '@/components/customers/detail/tabs/CustomerDetailFinanceAllTab.vue';
import CustomerDetailDocumentsTab from '@/components/customers/detail/tabs/CustomerDetailDocumentsTab.vue';
import CustomerDetailSettingsTab from '@/components/customers/detail/tabs/CustomerDetailSettingsTab.vue';
import CustomerDetailAccountStatementTab from '@/components/customers/detail/tabs/CustomerDetailAccountStatementTab.vue';
import CustomerDetailCashFlowTab from '@/components/customers/detail/tabs/CustomerDetailCashFlowTab.vue';
import CustomerDetailHistoryTab from '@/components/customers/detail/tabs/CustomerDetailHistoryTab.vue';
import salesService from '@/services/sales';
import salesFlatRatesService from '@/services/salesFlatRates';
import salesMultiCustomerService from '@/services/salesMultiCustomer';
import { extractCollection } from '@/utils/dataNormalization';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('customers');

const BUSINESS_SUB_TAB_KEYS = ['sales', 'retainers', 'multi-customer'];

const FINANCE_SUB_TABS = [
  { key: 'all', label: text.detail.subTabs.finance.all },
  { key: 'invoices', label: text.detail.subTabs.finance.invoices },
  { key: 'credit-notes', label: text.detail.subTabs.finance.creditNotes },
  { key: 'simple-notes', label: text.detail.subTabs.finance.simpleNotes },
  { key: 'payments', label: text.detail.subTabs.finance.payments },
];

const ANALYSIS_SUB_TABS = [
  { key: 'account-statement', label: text.detail.subTabs.analysis.accountStatement },
  { key: 'cash-flow', label: text.detail.subTabs.analysis.cashFlow },
];

const BUSINESS_COMPONENTS = {
  sales: CustomerDetailSalesTab,
  retainers: CustomerDetailRetainersTab,
  'multi-customer': CustomerDetailMultiCustomerTab,
};

const FINANCE_COMPONENTS = {
  all: CustomerDetailFinanceAllTab,
  invoices: CustomerDetailInvoicesTab,
  'credit-notes': CustomerDetailCreditNotesTab,
  'simple-notes': CustomerDetailSimpleNotesTab,
  payments: CustomerDetailPaymentsTab,
};

const ANALYSIS_COMPONENTS = {
  'account-statement': CustomerDetailAccountStatementTab,
  'cash-flow': CustomerDetailCashFlowTab,
};

const PRIMARY_COMPONENTS = {
  summary: CustomerDetailOverviewTab,
  information: CustomerDetailInformationTab,
  documents: CustomerDetailDocumentsTab,
  settings: CustomerDetailSettingsTab,
  history: CustomerDetailHistoryTab,
};

const ROUTE_TO_SUB_TAB = {
  sales: { parent: 'business', key: 'sales' },
  retainers: { parent: 'business', key: 'retainers' },
  'multi-customer': { parent: 'business', key: 'multi-customer' },
  multicliente: { parent: 'business', key: 'multi-customer' },
  all: { parent: 'finance', key: 'all' },
  invoices: { parent: 'finance', key: 'invoices' },
  'credit-notes': { parent: 'finance', key: 'credit-notes' },
  'simple-notes': { parent: 'finance', key: 'simple-notes' },
  payments: { parent: 'finance', key: 'payments' },
  'account-statement': { parent: 'analysis', key: 'account-statement' },
  'cash-flow': { parent: 'analysis', key: 'cash-flow' },
};

export default {
  name: 'CustomerDetailTabsPanel',
  components: {
    AppSectionTabs,
  },
  props: {
    activeTab: {
      type: String,
      required: true,
    },
    routeTab: {
      type: String,
      default: '',
    },
    customer: {
      type: Object,
      default: null,
    },
    customerMetrics: {
      type: Object,
      default: null,
    },
    customerOverview: {
      type: Object,
      default: null,
    },
    persons: {
      type: Array,
      default: () => [],
    },
    defaultBillingData: {
      type: Object,
      default: null,
    },
  },
  emits: [
    'edit-section',
    'add-contact',
    'edit-contact',
    'delete-contact',
    'edit-billing-data',
    'open-billing-data-list',
    'create-billing-data',
    'view-sale-detail',
    'change-sale-status',
    'open-history',
    'open-permissions',
    'open-merge',
    'archive-toggle',
    'delete-customer',
  ],
  data() {
    return {
      text,
      businessSubTab: 'sales',
      financeSubTab: 'all',
      analysisSubTab: 'account-statement',
      businessCounts: {
        sales: 0,
        retainers: 0,
        'multi-customer': 0,
      },
    };
  },
  computed: {
    showSubTabs() {
      return this.activeTab === 'business' || this.activeTab === 'finance' || this.activeTab === 'analysis';
    },
    currentSubTabs() {
      if (this.activeTab === 'business') {
        return BUSINESS_SUB_TAB_KEYS.map((key) => ({
          key,
          label: this.buildBusinessSubTabLabel(key),
        }));
      }

      if (this.activeTab === 'finance') {
        return FINANCE_SUB_TABS;
      }

      if (this.activeTab === 'analysis') {
        return ANALYSIS_SUB_TABS;
      }

      return [];
    },
    currentSubTab: {
      get() {
        if (this.activeTab === 'business') {
          return this.businessSubTab;
        }

        if (this.activeTab === 'finance') {
          return this.financeSubTab;
        }

        if (this.activeTab === 'analysis') {
          return this.analysisSubTab;
        }

        return '';
      },
      set(nextTab) {
        const normalized = String(nextTab || '').trim();
        if (normalized === '') {
          return;
        }

        if (this.activeTab === 'business' && BUSINESS_COMPONENTS[normalized]) {
          this.businessSubTab = normalized;
          return;
        }

        if (this.activeTab === 'finance' && FINANCE_COMPONENTS[normalized]) {
          this.financeSubTab = normalized;
          return;
        }

        if (this.activeTab === 'analysis' && ANALYSIS_COMPONENTS[normalized]) {
          this.analysisSubTab = normalized;
        }
      },
    },
    currentTabComponent() {
      if (this.activeTab === 'business') {
        return BUSINESS_COMPONENTS[this.businessSubTab] || CustomerDetailSalesTab;
      }

      if (this.activeTab === 'finance') {
        return FINANCE_COMPONENTS[this.financeSubTab] || CustomerDetailFinanceAllTab;
      }

      if (this.activeTab === 'analysis') {
        return ANALYSIS_COMPONENTS[this.analysisSubTab] || CustomerDetailAccountStatementTab;
      }

      return PRIMARY_COMPONENTS[this.activeTab] || CustomerDetailInformationTab;
    },
  },
  watch: {
    activeTab: {
      handler() {
        this.syncSubTabFromRoute();
      },
      immediate: true,
    },
    routeTab() {
      this.syncSubTabFromRoute();
    },
    customer: {
      handler() {
        this.businessCounts = {
          sales: 0,
          retainers: 0,
          'multi-customer': 0,
        };

        this.refreshBusinessCounts();
      },
      immediate: true,
      deep: false,
    },
  },
  methods: {
    resolveDistinctBusinessCount(rows = []) {
      const source = Array.isArray(rows) ? rows : [];
      const ids = new Set();

      source.forEach((row) => {
        const id = Number(row?.sale_id || row?.id || 0);
        if (id > 0) {
          ids.add(id);
        }
      });

      return ids.size;
    },
    async refreshBusinessCounts() {
      const customerId = Number(this.customer?.id || 0);
      if (customerId <= 0) {
        return;
      }

      const params = {
        customer: [String(customerId)],
      };

      const [salesResult, retainersResult, multiCustomerResult] = await Promise.allSettled([
        salesService.index(params, { skipLoading: true }),
        salesFlatRatesService.index(params, { skipLoading: true }),
        salesMultiCustomerService.index(params, { skipLoading: true }),
      ]);

      this.businessCounts = {
        sales: salesResult.status === 'fulfilled'
          ? this.resolveDistinctBusinessCount(extractCollection(salesResult.value))
          : 0,
        retainers: retainersResult.status === 'fulfilled'
          ? this.resolveDistinctBusinessCount(extractCollection(retainersResult.value))
          : 0,
        'multi-customer': multiCustomerResult.status === 'fulfilled'
          ? this.resolveDistinctBusinessCount(extractCollection(multiCustomerResult.value))
          : 0,
      };
    },
    buildBusinessSubTabLabel(tabKey) {
      const key = String(tabKey || '').trim();
      const count = Number(this.businessCounts?.[key] || 0);

      if (key === 'sales') {
        return `${this.text.detail.subTabs.business.sales}(${count})`;
      }

      if (key === 'retainers') {
        return `${this.text.detail.subTabs.business.retainers}(${count})`;
      }

      if (key === 'multi-customer') {
        return `${this.text.detail.subTabs.business.multiCustomer}(${count})`;
      }

      return `${key}(${count})`;
    },
    handleBusinessCountUpdate(payload) {
      const tabKey = String(payload?.key || '').trim();
      if (!BUSINESS_SUB_TAB_KEYS.includes(tabKey)) {
        return;
      }

      this.businessCounts = {
        ...this.businessCounts,
        [tabKey]: Number(payload?.count || 0),
      };
    },
    syncSubTabFromRoute() {
      const routeKey = String(this.routeTab || '').trim();
      if (routeKey === '') {
        return;
      }

      const mapped = ROUTE_TO_SUB_TAB[routeKey];
      if (!mapped || mapped.parent !== this.activeTab) {
        return;
      }

      this.currentSubTab = mapped.key;
    },
  },
};
</script>

<style scoped>
.customer-detail-tabs-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
</style>
